/**
 * Language.
 *
 * Keyed on the English sentence rather than on invented identifiers. In a
 * codebase of six hundred strings that were all written in English first, a key
 * scheme means inventing six hundred names, using each exactly twice, and
 * reading `t("gallery.empty.filtered.title")` where the sentence used to be —
 * which makes every component harder to read in order to make one file tidier.
 *
 * Keying on the source has a second property that matters more here: a string
 * with no translation falls back to itself. Coverage can therefore grow one
 * sentence at a time and the app is never broken in between, which is the
 * difference between a translation that ships and one that waits until it is
 * complete.
 *
 * The cost is that editing an English sentence silently orphans its
 * translation. `npm run i18n:check` reports entries no longer present in the
 * source, so the orphan is a build-time list rather than a surprise.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { pl } from "./locales/pl.js";

export const LOCALES = ["en", "pl"] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  pl: "Polski",
};

/** Every dictionary but English, which is the source and needs none. */
const DICTIONARIES: Record<
  Exclude<Locale, "en">,
  Readonly<Record<string, string>>
> = { pl };

const STORAGE_KEY = "fc:locale";

function isLocale(value: string | null): value is Locale {
  return value !== null && (LOCALES as readonly string[]).includes(value);
}

/**
 * What language to open in, before anybody has said.
 *
 * The browser's own preference list, in order, because it is the one place a
 * person has already answered this question — usually when they set up the
 * device, and for their whole system rather than for us. Matching on the
 * language subtag alone means `pl-PL`, `pl` and a Polish-preferring browser in
 * Germany all arrive in Polish.
 *
 * Deliberately not geolocation. Where somebody is standing is a poor guess at
 * what they read: a Pole on holiday in Spain does not want the site in Spanish,
 * and asking for location to decide a language would be an alarming permission
 * prompt in exchange for a worse answer.
 */
export function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Private windows and blocked storage: fall through to the browser's list.
  }

  const preferences =
    typeof navigator === "undefined"
      ? []
      : [...(navigator.languages ?? []), navigator.language].filter(Boolean);

  for (const preference of preferences) {
    const language = preference.toLowerCase().split("-")[0];
    if (isLocale(language ?? null)) return language as Locale;
  }

  return "en";
}

export type Translate = (text: string) => string;

interface LocaleValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translate;
}

const LocaleContext = createContext<LocaleValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale());

  // Screen readers and hyphenation both key off this, and it is wrong by
  // default: the document is served with `lang="en"` whatever we render.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Remembering is a convenience; the choice still applies to this session.
    }
  }, []);

  const t = useCallback<Translate>(
    (text) => {
      if (locale === "en") return text;
      return DICTIONARIES[locale][text] ?? text;
    },
    [locale],
  );

  const value = useMemo<LocaleValue>(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

/**
 * Translate.
 *
 * Falls back to English outside a provider rather than throwing. A missing
 * provider should not be able to blank a screen over a language setting, and
 * the failure mode — English text — is both obvious and harmless.
 */
export function useT(): Translate {
  const context = useContext(LocaleContext);
  return context?.t ?? ((text: string) => text);
}

export function useLocale(): {
  locale: Locale;
  setLocale: (locale: Locale) => void;
} {
  const context = useContext(LocaleContext);
  return {
    locale: context?.locale ?? "en",
    setLocale: context?.setLocale ?? (() => {}),
  };
}
